/**
 * Shared sub-components extracted from MappingStep.tsx.
 *
 * - ScrollableTableWrapper: horizontal scroll container with gradient fade indicators.
 * - SameRowPairCheckbox: checkbox for toggling same-row association pairs.
 *
 * ColumnHeader used to live here; it hand-rolled a label + help tooltip that
 * `Table.Head` from `@/lib/base/table` provides natively via its `label` and
 * `tooltip` props, so it was removed when the mapping grid moved to that table.
 */

import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { cx } from '@/utils/cx';

// ============================================================================
// ScrollableTableWrapper
// ============================================================================

/**
 * Horizontal scroll wrapper for mapping tables that adds gradient fade
 * indicators when content overflows. Mobile-only in practice since the
 * desktop detail pane is wide enough, but the fades are harmless if they
 * never appear.
 */
export function ScrollableTableWrapper({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const left = scrollLeft > 1;
      const right = scrollLeft + clientWidth < scrollWidth - 1;
      setEdges(prev =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) {
      ro.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  return (
    <div className='relative'>
      <div ref={scrollRef} className='overflow-x-auto'>
        {children}
      </div>
      <div
        aria-hidden
        className={cx(
          'pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-bg-primary to-transparent transition-opacity duration-150',
          edges.left ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        className={cx(
          'pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-bg-primary to-transparent transition-opacity duration-150',
          edges.right ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
}

// ============================================================================
// SameRowPairCheckbox
// ============================================================================

export interface SameRowPairCheckboxProps {
  fileId: string | number;
  pairKey: string;
  sourceLabel: string;
  targetLabel: string;
  isEnabled: boolean;
  onChange: (
    fileId: string | number,
    pairKey: string,
    isEnabled: boolean
  ) => void;
}

export const SameRowPairCheckbox = memo(function SameRowPairCheckbox({
  fileId,
  pairKey,
  sourceLabel,
  targetLabel,
  isEnabled,
  onChange,
}: SameRowPairCheckboxProps) {
  const handleChange = useCallback(
    (selected: boolean) => onChange(fileId, pairKey, selected),
    [onChange, fileId, pairKey]
  );
  const accessibleName = `Link ${sourceLabel} and ${targetLabel}`;
  return (
    <Checkbox
      size='sm'
      isSelected={isEnabled}
      onChange={handleChange}
      aria-label={accessibleName}
      label={
        <span>
          {sourceLabel}
          <span aria-hidden='true'>{' ↔ '}</span>
          {targetLabel}
        </span>
      }
    />
  );
});
SameRowPairCheckbox.displayName = 'SameRowPairCheckbox';
